"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api, parseApiError, type NotificationPrefs } from "@/lib/api";
import { getToken, clearToken } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { CardIcon } from "@/components/CardIcon";
import { PageHelp } from "@/components/PageHelp";
import { ApiKeyHelpModal } from "@/components/ApiKeyHelpModal";
import { locales, localeNames, type Locale } from "@/i18n/config";
import { getTheme, setTheme as persistTheme, getAccent, setAccent as persistAccent, ACCENTS, ACCENT_HEX, type Theme, type Accent } from "@/lib/theme";

type MeData = { id: number; name: string; email: string; has_api_key: boolean; api_key_preview: string | null };

// Ayarlar kategorileri (sekme yapısı). Aktif sekme URL hash'inde tutulur
// (/settings#preferences gibi) — yenileme ve doğrudan link çalışır.
const SETTINGS_TABS = ["account", "preferences", "api"] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];

function tabFromHash(): SettingsTab {
  if (typeof window === "undefined") return "account";
  const h = window.location.hash.replace("#", "");
  return (SETTINGS_TABS as readonly string[]).includes(h) ? (h as SettingsTab) : "account";
}

export default function SettingsPage() {
  const router = useRouter();
  const t = useTranslations("settings");
  const locale = useLocale();
  const [me, setMe] = useState<MeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<SettingsTab>("account");

  function switchTab(next: SettingsTab) {
    setTab(next);
    // router.push yerine hash replace: sayfayı yeniden yüklemeden URL'i güncelle.
    window.history.replaceState(null, "", `#${next}`);
  }

  const [apiKey, setApiKey] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [showApiHelp, setShowApiHelp] = useState(false);
  const [theme, setThemeState] = useState<Theme>("light");
  const [accent, setAccentState] = useState<Accent>("mint");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwStatus, setPwStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [pwError, setPwError] = useState("");

  // Notification preferences. Default both on; if the backend endpoint isn't
  // live yet (GET 404s) we keep these defaults rather than crashing the page.
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>({ session_feedback: true, product_updates: true });
  const [notifStatus, setNotifStatus] = useState<"idle" | "saving" | "success" | "error">("idle");

  // Hesap silme (mail doğrulamalı, iki adımlı): idle → confirm (kod istendi) input.
  const [delStage, setDelStage] = useState<"idle" | "code">("idle");
  const [delCode, setDelCode] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");
  const [delInfo, setDelInfo] = useState("");

  useEffect(() => {
    setThemeState(getTheme());
    setAccentState(getAccent());
    setTab(tabFromHash());
    // Hash-only gezinme (geri/ileri, sayfa açıkken #hash linki) remount tetiklemez;
    // sekmeyi hashchange ile de senkron tut.
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  function switchTheme(next: Theme) {
    if (next === theme) return;
    persistTheme(next);
    setThemeState(next);
  }

  function switchAccent(next: Accent) {
    if (next === accent) return;
    persistAccent(next);
    setAccentState(next);
  }

  function switchLocale(next: Locale) {
    if (next === locale) return;
    document.cookie = `NEXT_LOCALE=${next}; path=/; max-age=31536000; SameSite=Lax`;
    router.refresh();
  }

  useEffect(() => {
    if (!getToken()) {
      router.push("/login");
      return;
    }
    api
      .getMe()
      .then((data) => setMe(data))
      .catch(() => router.push("/login"))
      .finally(() => setLoading(false));
    // Fetch notification prefs separately; a 404 (endpoint not yet migrated)
    // must not block or fail the settings page — keep the optimistic defaults.
    api
      .getNotificationPrefs()
      .then((prefs) => setNotifPrefs(prefs))
      .catch(() => {});
  }, [router]);

  async function handleSaveKey() {
    if (!apiKey.trim() || saveStatus === "saving") return;
    setSaveStatus("saving");
    setErrorMsg("");
    try {
      // Backend validates the key against Anthropic before storing it; an
      // invalid/unverifiable key throws here and is never saved.
      await api.updateApiKey(apiKey.trim());
      setSaveStatus("success");
      setApiKey("");
      const updated = await api.getMe();
      setMe(updated);
    } catch (err: unknown) {
      const { code, message } = parseApiError(err);
      setSaveStatus("error");
      setErrorMsg(
        code === "invalid_api_key" ? t("apiKey.invalid")
        : code === "api_key_unverifiable" ? t("apiKey.unverifiable")
        : message || t("apiKey.errorFallback")
      );
    }
  }

  async function handleDeleteKey() {
    if (!confirm(t("apiKey.deleteConfirm"))) return;
    setSaveStatus("idle");
    setErrorMsg("");
    try {
      await api.deleteApiKey();
      setApiKey("");
      const updated = await api.getMe();
      setMe(updated);
    } catch (err: unknown) {
      setSaveStatus("error");
      setErrorMsg(parseApiError(err).message || t("apiKey.errorFallback"));
    }
  }

  async function handleChangePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) return;
    if (newPassword.length < 8) {
      setPwStatus("error");
      setPwError(t("password.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwStatus("error");
      setPwError(t("password.mismatch"));
      return;
    }
    setPwStatus("saving");
    setPwError("");
    try {
      await api.changePassword(currentPassword, newPassword);
      setPwStatus("success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      setPwStatus("error");
      const msg = err instanceof Error ? err.message : "";
      setPwError(
        msg.includes("incorrect") ? t("password.incorrect")
        : msg.includes("different") ? t("password.notDifferent")
        : t("password.errorFallback")
      );
    }
  }

  async function handleSaveNotifications(next: NotificationPrefs) {
    const previous = notifPrefs;
    setNotifPrefs(next);
    setNotifStatus("saving");
    try {
      const saved = await api.updateNotificationPrefs(next);
      setNotifPrefs(saved);
      setNotifStatus("success");
    } catch {
      setNotifPrefs(previous);
      setNotifStatus("error");
    }
  }

  async function handleRequestDeletion() {
    setDelBusy(true);
    setDelError("");
    setDelInfo("");
    try {
      await api.requestAccountDeletion(locale);
      setDelStage("code");
      setDelInfo(t("deleteAccount.codeSent"));
    } catch (err: unknown) {
      const e = parseApiError(err);
      setDelError(e.code === "resend_throttled" ? e.message : t("deleteAccount.error"));
    } finally {
      setDelBusy(false);
    }
  }

  async function handleConfirmDeletion() {
    const clean = delCode.replace(/\D/g, "");
    if (clean.length !== 6) {
      setDelError(t("deleteAccount.codeRequired"));
      return;
    }
    setDelBusy(true);
    setDelError("");
    try {
      await api.confirmAccountDeletion(clean);
      clearToken();
      router.replace("/login");
    } catch (err: unknown) {
      const c = parseApiError(err).code;
      if (c === "invalid_code") setDelError(t("deleteAccount.invalidCode"));
      else if (c === "code_expired" || c === "too_many_attempts") setDelError(t("deleteAccount.expired"));
      else setDelError(t("deleteAccount.error"));
      setDelBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 bg-[var(--bg)]">
        <div className="flex items-center gap-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2.5 h-2.5 rounded-full bg-[var(--accent)] animate-bounce"
              style={{ animationDelay: `${i * 0.18}s`, animationDuration: "0.9s" }}
            />
          ))}
        </div>
        <p className="text-[var(--text-secondary)] text-sm">{t("loading")}</p>
      </div>
    );
  }

  return (
    <AppShell active="settings">
      {showApiHelp && <ApiKeyHelpModal onClose={() => setShowApiHelp(false)} />}

      <main className="max-w-2xl mx-auto px-5 py-10">
        {/* Page heading */}
        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("title")}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {t("subtitle")}
            </p>
          </div>
          <PageHelp topic="settings" />
        </div>

        {/* Kategori sekmeleri — ayarlar çoğaldıkça tek sütun gezinmesi zorlaştı */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {SETTINGS_TABS.map((k) => (
            <button
              key={k}
              onClick={() => switchTab(k)}
              className={
                k === tab
                  ? "rounded-xl border px-4 py-2 text-sm font-semibold border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)]"
                  : "rounded-xl border px-4 py-2 text-sm font-semibold border-[var(--border)] bg-[var(--surface-alt)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--text-primary)] transition-colors"
              }
            >
              {t(`tabs.${k}`)}
            </button>
          ))}
        </div>

        {/* Profile section */}
        <section hidden={tab !== "account"} className="bg-white rounded-2xl border border-[var(--border)] p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-5">
            <CardIcon type="profile" size={22} />
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("profile.title")}
            </h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {t("profile.name")}
              </label>
              <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)]">
                {me?.name}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {t("profile.email")}
              </label>
              <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)]">
                {me?.email}
              </div>
            </div>
          </div>
        </section>

        {/* Language section */}
        <section hidden={tab !== "preferences"} className="bg-white rounded-2xl border border-[var(--border)] p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-1">
            <CardIcon type="language" size={22} />
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("language.title")}
            </h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            {t("language.description")}
          </p>
          <div className="flex gap-2">
            {locales.map((l) => (
              <button
                key={l}
                onClick={() => switchLocale(l)}
                className={
                  l === locale
                    ? "rounded-xl border px-4 py-2.5 text-sm font-semibold border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)]"
                    : "rounded-xl border px-4 py-2.5 text-sm font-semibold border-[var(--border)] bg-[var(--surface-alt)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--text-primary)] transition-colors"
                }
              >
                {localeNames[l]}
              </button>
            ))}
          </div>
        </section>

        {/* Appearance section */}
        <section hidden={tab !== "preferences"} className="bg-white rounded-2xl border border-[var(--border)] p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-1">
            <CardIcon type="appearance" size={22} />
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("appearance.title")}
            </h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            {t("appearance.description")}
          </p>
          <div className="flex gap-2">
            {([
              {
                value: "light" as Theme,
                label: t("appearance.light"),
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                ),
              },
              {
                value: "dark" as Theme,
                label: t("appearance.dark"),
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                ),
              },
            ]).map((opt) => (
              <button
                key={opt.value}
                onClick={() => switchTheme(opt.value)}
                className={
                  opt.value === theme
                    ? "flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)]"
                    : "flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold border-[var(--border)] bg-[var(--surface-alt)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--text-primary)] transition-colors"
                }
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  {opt.icon}
                </svg>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Accent color */}
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mt-5 mb-2.5">
            {t("appearance.accent")}
          </p>
          <div className="flex gap-3">
            {ACCENTS.map((a) => (
              <button
                key={a}
                onClick={() => switchAccent(a)}
                title={a}
                aria-label={a}
                className={
                  "w-8 h-8 rounded-full transition-transform hover:scale-110 ring-offset-2 ring-offset-[var(--surface)] " +
                  (a === accent ? "ring-2 ring-[var(--text-primary)]" : "ring-0")
                }
                style={{ background: ACCENT_HEX[a] }}
              />
            ))}
          </div>
        </section>

        {/* API Key section */}
        <section hidden={tab !== "api"} className="bg-white rounded-2xl border border-[var(--border)] p-6">
          <div className="flex items-center gap-3 mb-5">
            <CardIcon type="apiKey" size={22} />
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("apiKey.title")}
            </h2>
            <button
              onClick={() => setShowApiHelp(true)}
              title={t("apiKey.helpTooltip")}
              className="flex-shrink-0 w-5 h-5 rounded-full border border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)] text-xs font-bold hover:bg-[var(--accent)] hover:text-white transition-colors flex items-center justify-center"
            >
              ?
            </button>
            {me?.has_api_key && (
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-200">
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t("apiKey.saved")}
              </span>
            )}
          </div>

          <div className="space-y-4">
            {me?.has_api_key && me?.api_key_preview && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5">
                <code className="text-sm text-[var(--text-primary)] tracking-wide">{me.api_key_preview}</code>
                <button
                  onClick={handleDeleteKey}
                  className="flex-shrink-0 text-sm font-semibold text-red-500 hover:text-red-600 transition-colors"
                >
                  {t("apiKey.deleteButton")}
                </button>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {me?.has_api_key ? t("apiKey.replaceLabel") : t("apiKey.enterLabel")}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  if (saveStatus !== "idle") setSaveStatus("idle");
                }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveKey(); } }}
                placeholder="sk-ant-..."
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)] transition-all"
              />
              <p className="mt-2 text-xs text-[var(--text-muted)] leading-relaxed">
                {t("apiKey.hint")}{" "}
                <a
                  href="https://console.anthropic.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--accent)] underline underline-offset-2 hover:opacity-80"
                >
                  console.anthropic.com
                </a>
                <button
                  onClick={() => setShowApiHelp(true)}
                  title={t("apiKey.helpTooltip")}
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)] font-bold hover:bg-[var(--accent)] hover:text-white transition-colors ml-1 align-middle"
                  style={{ fontSize: "9px" }}
                >
                  ?
                </button>
              </p>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSaveKey}
                disabled={!apiKey.trim() || saveStatus === "saving"}
                className="shrink-0 whitespace-nowrap inline-flex items-center justify-center min-w-[170px] rounded-xl text-white px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'var(--accent)', boxShadow: '0 2px 8px rgba(15,76,76,0.15)' }}
                onMouseOver={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent-mid)'; }}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
              >
                {saveStatus === "saving" ? t("apiKey.saving") : t("apiKey.saveButton")}
              </button>

              {saveStatus === "success" && (
                <span className="text-sm text-emerald-600 font-medium">
                  {t("apiKey.success")}
                </span>
              )}
              {saveStatus === "error" && (
                <span className="text-sm text-red-500">
                  {errorMsg || t("apiKey.error")}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Password section */}
        <section hidden={tab !== "account"} className="bg-white rounded-2xl border border-[var(--border)] p-6 mt-5">
          <div className="flex items-center gap-2.5 mb-1">
            <CardIcon type="password" size={22} />
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("password.title")}
            </h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            {t("password.description")}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {t("password.current")}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (pwStatus !== "idle") setPwStatus("idle");
                }}
                placeholder="••••••••"
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)] transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {t("password.new")}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (pwStatus !== "idle") setPwStatus("idle");
                }}
                placeholder="••••••••"
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)] transition-all"
              />
              <p className="mt-2 text-xs text-[var(--text-muted)] leading-relaxed">
                {t("password.hint")}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                {t("password.confirm")}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (pwStatus !== "idle") setPwStatus("idle");
                }}
                placeholder="••••••••"
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)] transition-all"
              />
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleChangePassword}
                disabled={!currentPassword || !newPassword || !confirmPassword || pwStatus === "saving"}
                className="rounded-xl text-white px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'var(--accent)', boxShadow: '0 2px 8px rgba(15,76,76,0.15)' }}
                onMouseOver={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent-mid)'; }}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
              >
                {pwStatus === "saving" ? t("password.saving") : t("password.saveButton")}
              </button>

              {pwStatus === "success" && (
                <span className="text-sm text-emerald-600 font-medium">
                  {t("password.success")}
                </span>
              )}
              {pwStatus === "error" && (
                <span className="text-sm text-red-500">
                  {pwError || t("password.errorFallback")}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Notifications section */}
        <section hidden={tab !== "preferences"} className="bg-white rounded-2xl border border-[var(--border)] p-6 mt-5">
          <div className="flex items-center gap-2.5 mb-1">
            <svg className="w-[22px] h-[22px] shrink-0 text-[var(--accent)]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
            </svg>
            <h2
              className="text-base font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("notifications.title")}
            </h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            {t("notifications.desc")}
          </p>

          <div className="space-y-3">
            {([
              { key: "session_feedback" as const, label: t("notifications.sessionFeedback") },
              { key: "product_updates" as const, label: t("notifications.productUpdates") },
            ]).map((opt) => (
              <label
                key={opt.key}
                className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-3 cursor-pointer hover:border-[var(--accent)] transition-colors"
              >
                <input
                  type="checkbox"
                  checked={notifPrefs[opt.key]}
                  onChange={(e) =>
                    handleSaveNotifications({ ...notifPrefs, [opt.key]: e.target.checked })
                  }
                  className="h-4 w-4 shrink-0 rounded border-[var(--border)] text-[var(--accent)] focus:ring-[var(--accent-light)] accent-[var(--accent)]"
                />
                <span className="text-sm text-[var(--text-primary)]">{opt.label}</span>
              </label>
            ))}

            <div className="flex items-center gap-3 pt-1 min-h-[20px]">
              {notifStatus === "success" && (
                <span className="text-sm text-emerald-600 font-medium">
                  {t("notifications.saved")}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Danger zone — hesap silme (mail doğrulamalı) */}
        <section hidden={tab !== "account"} className="bg-white rounded-2xl border border-red-200 p-6 mt-5">
          <div className="flex items-center gap-2.5 mb-1">
            <svg className="w-[22px] h-[22px] shrink-0 text-red-500" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
            <h2 className="text-base font-semibold text-red-600" style={{ fontFamily: "var(--font-lora)" }}>
              {t("deleteAccount.title")}
            </h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            {t("deleteAccount.description")}
          </p>

          {delError && <p className="text-sm text-red-500 mb-3">{delError}</p>}
          {delInfo && <p className="text-sm text-emerald-600 mb-3">{delInfo}</p>}

          {delStage === "idle" ? (
            <button
              onClick={handleRequestDeletion}
              disabled={delBusy}
              className="rounded-xl border border-red-300 text-red-600 px-5 py-2.5 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-40"
            >
              {delBusy ? t("deleteAccount.sending") : t("deleteAccount.requestButton")}
            </button>
          ) : (
            <div className="space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                {t("deleteAccount.codeLabel")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={delCode}
                onChange={(e) => setDelCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="w-40 rounded-xl border border-red-200 bg-[var(--surface-alt)] px-4 py-2.5 text-center text-lg tracking-[0.4em] font-semibold text-[var(--text-primary)] focus:outline-none focus:border-red-400 transition-all"
              />
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={handleConfirmDeletion}
                  disabled={delBusy || delCode.length !== 6}
                  className="rounded-xl bg-red-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40"
                >
                  {delBusy ? t("deleteAccount.deleting") : t("deleteAccount.confirmButton")}
                </button>
                <button
                  onClick={() => { setDelStage("idle"); setDelCode(""); setDelError(""); setDelInfo(""); }}
                  disabled={delBusy}
                  className="text-sm text-[var(--text-muted)] hover:underline"
                >
                  {t("deleteAccount.cancel")}
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </AppShell>
  );
}
